import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';

import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';

import { SharedModule } from '../../shared/shared.module';
import { DialogConfig } from '../../shared/components/reusable-dialog/form-field.interface';

import { ApiService } from '../../services/api.service';
import { PermissionsService } from '../../services/permission.service';

export interface WorkLog {
  id?: number;
  title: string;
  ticket?: number;
  status: string;
  description: string;
  date?: Date;
}

@Component({
  selector: 'app-task-management',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    ToastModule,
    SharedModule
  ],
  templateUrl: './task-management.component.html',
  styleUrls: ['./task-management.component.css']
})
export class TaskManagementComponent implements OnInit, OnDestroy {

  selectedDate: Date = new Date();
  currentMonth: number = new Date().getMonth();
  currentYear: number = new Date().getFullYear();

  showDialog = false;
  viewMode = false;

  formData: any = {};
  selectedsystem: any = null;

  workLogs: WorkLog[] = [];

  private subscriptions = new Subscription();

  constructor(
    private api: ApiService,
    private messageService: MessageService,
    private permissionService: PermissionsService
  ) {}

  dialogConfig: DialogConfig = {
    title: 'Create Work Log',
    width: '60vw',
    height: 'auto',
    maximizable: true,
    closable: true,
    dismissableMask: true,
    formLayout: 'single-column',

    fields: [
      {
        key: 'title',
        label: 'Title',
        type: 'text',
        required: true,
        placeholder: 'Enter work log title',
        validation: { required: true }
      },
      {
  key: 'ticket',
  label: 'Ticket',
  type: 'select',
  required: true,
  options: []
},
      {
        key: 'status',
        label: 'Status',
        type: 'select',
        options: [
          { label: 'PENDING', value: 'PENDING' },
          { label: 'IN_PROGRESS', value: 'IN_PROGRESS' },
          { label: 'COMPLETED', value: 'COMPLETED' },
        ]
      },
      {
        key: 'description',
        label: 'Description',
        type: 'textarea',
        required: true,
        placeholder: 'Work description...',
        maxLength: 500,
        validation: { required: true }
      }
    ],

    submitButtonText: 'Submit',
    cancelButtonText: 'Cancel',
    showCancelButton: true
  };

  ngOnInit(): void {
    this.getTasks();
     this.getTicketList();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  /* --------------------------
     API CALLS
  -------------------------- */

  getTasks() {

    const sub = this.api.getAPI('/ticket/daily-tasks/').subscribe((res: any) => {

      if (res && res.data) {

        this.workLogs = res.data.map((item: any) => ({
          id: item.id,
          title: item.title,
          ticket: item.ticket,
          description: item.description,
          status: item.status,
          date: new Date(item.date)
        }));

      }

    });

    this.subscriptions.add(sub);
  }

 onSubmitWorkLog(formData: any) {

  let payload: any = {

    ticket: formData.ticket, // <-- ticket ID
    title: formData.title,
    description: formData.description,
     status: formData.status ? formData.status.toLowerCase() : 'pending',
    date: this.formatDate(this.selectedDate)

  };

  // update case
  if (this.selectedsystem && this.selectedsystem.id) {
    payload.id = this.selectedsystem.id;
  }

  const sub = this.api.postAPI('/ticket/daily-tasks/', payload)
  .subscribe((res: any) => {

    this.messageService.add({
      severity: 'success',
      summary: 'Success',
      detail: this.selectedsystem ? 'Task updated' : 'Task created'
    });

    this.showDialog = false;
    this.selectedsystem = null;

    this.getTasks();

  });

  this.subscriptions.add(sub);
}

onDeleteWorkLog(workLog: any) {

  const payload = {
    id: workLog.id,
    delete: true
  };

  const sub = this.api.postAPI('/ticket/daily-tasks/', payload)
  .subscribe((res: any) => {

    this.messageService.add({
      severity: 'success',
      summary: 'Deleted',
      detail: 'Task deleted successfully'
    });

    this.getTasks(); // reload list

  });

  this.subscriptions.add(sub);
}

  /* --------------------------
     FORM ACTIONS
  -------------------------- */

  addSystem() {
    this.viewMode = false;
    this.selectedsystem = null;
    this.formData = {};

    this.dialogConfig.title = 'Create Work Log';
    this.dialogConfig.submitButtonText = 'Submit';

    this.showDialog = true;
  }

onEditWorkLog(workLog: any) {

  this.selectedsystem = workLog;

  this.formData = {
    title: workLog.title,
    ticket: workLog.ticket,
    status: workLog.status ? workLog.status.toUpperCase() : 'PENDING',
    description: workLog.description
  };

  this.dialogConfig.title = 'Update Work Log';
  this.dialogConfig.submitButtonText = 'Update';

  this.showDialog = true;
}

  onCancelDialog() {
    this.showDialog = false;
    this.selectedsystem = null;
    this.formData = {};
  }

  onCloseDialog() {
    this.showDialog = false;
    this.selectedsystem = null;
    this.formData = {};
  }

  /* --------------------------
     DATE UTILITIES
  -------------------------- */

  formatDate(date: Date): string {

    const year = date.getFullYear();
    const month = ('0' + (date.getMonth() + 1)).slice(-2);
    const day = ('0' + date.getDate()).slice(-2);

    return `${year}-${month}-${day}`;
  }

  getWorkLogsForSelectedDate(): WorkLog[] {

    return this.workLogs.filter(log => {

      if (!log.date) return false;

      const d = new Date(log.date);

      return (
        d.getDate() === this.selectedDate.getDate() &&
        d.getMonth() === this.selectedDate.getMonth() &&
        d.getFullYear() === this.selectedDate.getFullYear()
      );

    });
  }

  /* --------------------------
     CALENDAR FUNCTIONS
  -------------------------- */

  getMonthName(): string {
    const months = [
      'January','February','March','April','May','June',
      'July','August','September','October','November','December'
    ];
    return months[this.currentMonth];
  }

  getDaysInMonth(): number {
    return new Date(this.currentYear, this.currentMonth + 1, 0).getDate();
  }

  getFirstDayOfMonth(): number {
    return new Date(this.currentYear, this.currentMonth, 1).getDay();
  }

  getCalendarDays(): (number | null)[] {

    const days: (number | null)[] = [];

    const firstDay = this.getFirstDayOfMonth();
    const daysInMonth = this.getDaysInMonth();

    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }

    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }

    return days;
  }

  selectDate(day: number | null) {

    if (!day) return;

    this.selectedDate = new Date(this.currentYear, this.currentMonth, day);
  }

  previousMonth() {

    if (this.currentMonth === 0) {
      this.currentMonth = 11;
      this.currentYear--;
    } else {
      this.currentMonth--;
    }

  }

  nextMonth() {

    if (this.currentMonth === 11) {
      this.currentMonth = 0;
      this.currentYear++;
    } else {
      this.currentMonth++;
    }

  }

  goToToday() {

    const today = new Date();

    this.currentMonth = today.getMonth();
    this.currentYear = today.getFullYear();
    this.selectedDate = today;

  }

  getSelectedDateString(): string {

  const days = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

  const dayName = days[this.selectedDate.getDay()];
  const monthName = months[this.selectedDate.getMonth()];

  return `${dayName} ${monthName} ${this.selectedDate.getDate()} ${this.selectedDate.getFullYear()}`;

}

isSelected(day: number | null): boolean {

  if (day === null) return false;

  return (
    day === this.selectedDate.getDate() &&
    this.currentMonth === this.selectedDate.getMonth() &&
    this.currentYear === this.selectedDate.getFullYear()
  );

}

isToday(day: number | null): boolean {

  if (day === null) return false;

  const today = new Date();

  return (
    day === today.getDate() &&
    this.currentMonth === today.getMonth() &&
    this.currentYear === today.getFullYear()
  );

}

ticketOptions: any[] = [];
getTicketList() {

  const sub = this.api.getAPI('/ticket/details/').subscribe((res: any) => {

    if (res && res.data) {

      this.ticketOptions = res.data.map((item: any) => ({
        label: item.ticket_number + ' - ' + item.title,
        value: item.id
      }));

      // update dropdown options dynamically
      const ticketField = this.dialogConfig.fields.find(f => f.key === 'ticket');

      if (ticketField) {
        ticketField.options = this.ticketOptions;
      }

    }

  });

  this.subscriptions.add(sub);
}
}